import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import styles from './OutreachDetailPage.module.scss'
import CustomModal from '../../../components/Shared/Modals/CustomModal'
import DeleteContent from '../../../components/Shared/Modals/DeleteContent'
import outreachService from '../../../supabaseServices/OutreachService'
import { setSnackbar } from '../../../store/snackbar/actions'
import { makeSelectUser } from '../../../store/auth/selectors'

interface RouteParams {
  uuid: string
}

interface OutreachDraft {
  uuid: string
  trip_uuid: string
  recipient_uuid: string
  recipient_email: string
  recipient_name: string | null
  destination: string | null
  trip_start_date: string | null
  email_subject: string
  email_body: string
  status: string
  notes: string | null
  created_at: string
  updated_at: string | null
  sent_at: string | null
  sent_by: string | null
  sequence_uuid: string | null
  sequence_step: number | null
  email_type: string | null
  scheduled_for: string | null
  confidence_score: number | null
  ab_variant: string | null
  trips: {
    trip_name: string
    trip_type: string
    location: string
    start_date: string
    end_date: string
  } | null
}

interface SequenceDraft {
  uuid: string
  email_subject: string
  email_type: string | null
  sequence_step: number | null
  status: string
  scheduled_for: string | null
  created_at: string
  sent_at: string | null
  confidence_score: number | null
  ab_variant: string | null
}

const EMAIL_TYPE_LABELS: Record<string, string> = {
  value: 'Value / Trust',
  gap: 'Gap Detection',
  protection: 'Protection / Trust',
}

function OutreachDetailPage() {
  const { uuid } = useParams<RouteParams>()
  const history = useHistory()
  const dispatch = useDispatch()
  const profileUUID: string | undefined = useSelector(makeSelectUser())?.uuid

  const [draft, setDraft] = useState<OutreachDraft | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)
  const [regenerating, setRegenerating] = useState<boolean>(false)

  const [emailSubject, setEmailSubject] = useState<string>('')
  const [emailBody, setEmailBody] = useState<string>('')
  const [notes, setNotes] = useState<string>('')

  const [savedSubject, setSavedSubject] = useState<string>('')
  const [savedBody, setSavedBody] = useState<string>('')
  const [savedNotes, setSavedNotes] = useState<string>('')

  const [showSendModal, setShowSendModal] = useState<boolean>(false)
  const [showSkipModal, setShowSkipModal] = useState<boolean>(false)
  const [sendingTest, setSendingTest] = useState<boolean>(false)
  const [testEmail, setTestEmail] = useState<string>('seth@avosquado.com')
  const [trackingEvents, setTrackingEvents] = useState<
    Array<{ type: string; received_at: string }>
  >([])
  const [trackingStatus, setTrackingStatus] = useState<string | null>(null)
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false)

  // Sequence state
  const [sequenceDrafts, setSequenceDrafts] = useState<SequenceDraft[]>([])
  const [loadingSequence, setLoadingSequence] = useState<boolean>(false)

  const fetchDraft = useCallback(async () => {
    setLoading(true)
    const data: OutreachDraft | null = await outreachService.getOutreachByUUID(uuid)
    if (data) {
      setDraft(data)
      setEmailSubject(data.email_subject)
      setEmailBody(data.email_body)
      setNotes(data.notes || '')
      setSavedSubject(data.email_subject)
      setSavedBody(data.email_body)
      setSavedNotes(data.notes || '')
    }
    setLoading(false)
  }, [uuid])

  const fetchSequence = useCallback(async (sequenceUuid: string) => {
    setLoadingSequence(true)
    const drafts: SequenceDraft[] = await outreachService.getSequenceDrafts(sequenceUuid)
    setSequenceDrafts(drafts)
    setLoadingSequence(false)
  }, [])

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true)
    const result = await outreachService.getOutreachEvents(uuid)
    if (result) {
      setTrackingStatus(result.status)
      setTrackingEvents(result.events)
    }
    setLoadingEvents(false)
  }, [uuid])

  useEffect(() => {
    fetchDraft()
  }, [fetchDraft])

  useEffect(() => {
    if (draft?.sequence_uuid) {
      fetchSequence(draft.sequence_uuid)
    }
  }, [draft?.sequence_uuid, fetchSequence])

  useEffect(() => {
    if (draft?.status === 'SENT') {
      fetchEvents()
    }
  }, [draft?.status, fetchEvents])

  const isDraft: boolean = draft?.status === 'DRAFT'
  const hasUnsavedChanges: boolean =
    emailSubject !== savedSubject || emailBody !== savedBody || notes !== savedNotes
  const canRegenerate: boolean =
    isDraft && (draft?.email_type === 'gap' || draft?.email_type === 'protection')
  const isReadyToSend: boolean =
    isDraft && !!draft?.scheduled_for && new Date(draft.scheduled_for) <= new Date()

  const handleSave = async () => {
    if (!draft) return
    setSaving(true)
    const success: boolean = await outreachService.updateOutreach(draft.uuid, {
      email_subject: emailSubject,
      email_body: emailBody,
      notes: notes || undefined,
    })
    setSaving(false)

    if (success) {
      setSavedSubject(emailSubject)
      setSavedBody(emailBody)
      setSavedNotes(notes)
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Draft saved successfully.',
        }),
      )
    } else {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Failed to save draft.',
        }),
      )
    }
  }

  const handleSend = async () => {
    if (!draft || !profileUUID) return
    setShowSendModal(false)
    setSending(true)

    const result: { success: boolean; error?: string } = await outreachService.sendOutreach(
      draft.uuid,
      profileUUID,
    )
    setSending(false)

    if (result.success) {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: `Email sent to ${draft.recipient_email}!`,
        }),
      )
      fetchDraft()
    } else {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: result.error || 'Failed to send email.',
        }),
      )
    }
  }

  const handleSkip = async () => {
    if (!draft) return
    setShowSkipModal(false)
    const success: boolean = await outreachService.skipOutreach(draft.uuid)

    if (success) {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Outreach skipped.',
        }),
      )
      fetchDraft()
    } else {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Failed to skip outreach.',
        }),
      )
    }
  }

  const handleRegenerate = async () => {
    if (!draft) return
    setRegenerating(true)

    const result: { success: boolean; error?: string } =
      await outreachService.regenerateOutreach(draft.uuid)
    setRegenerating(false)

    if (result.success) {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Email regenerated with fresh gap detection.',
        }),
      )
      fetchDraft()
    } else {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: result.error || 'Failed to regenerate email.',
        }),
      )
    }
  }

  const handleSendTest = async () => {
    if (!draft || !profileUUID) return
    setSendingTest(true)

    const result: { success: boolean; error?: string } = await outreachService.sendTestEmail(
      draft.uuid,
      testEmail,
      profileUUID,
    )
    setSendingTest(false)

    if (result.success) {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: `Test email sent to ${testEmail}`,
        }),
      )
    } else {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: result.error || 'Failed to send test email.',
        }),
      )
    }
  }

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return styles.statusDraft
      case 'SENT':
        return styles.statusSent
      case 'SKIPPED':
        return styles.statusSkipped
      default:
        return ''
    }
  }

  if (loading) {
    return <div className={styles.loading}>Loading...</div>
  }

  if (!draft) {
    return <div className={styles.loading}>Outreach draft not found.</div>
  }

  return (
    <div className={styles.mainWrapper}>
      <CustomModal
        maxWidth="sm"
        title="Send Email"
        closeModal={() => setShowSendModal(false)}
        isVisible={showSendModal}
        children={
          <DeleteContent
            confirmButtonColor="#28a745"
            onPressConfirm={handleSend}
            onPressClose={() => setShowSendModal(false)}
            text={`Send this email to ${draft.recipient_email}? This action cannot be undone.`}
          />
        }
      />

      <CustomModal
        maxWidth="sm"
        title="Skip Outreach"
        closeModal={() => setShowSkipModal(false)}
        isVisible={showSkipModal}
        children={
          <DeleteContent
            confirmButtonColor="gray"
            onPressConfirm={handleSkip}
            onPressClose={() => setShowSkipModal(false)}
            text="Are you sure you want to skip this outreach? The email will not be sent."
          />
        }
      />

      <button className={styles.backButton} onClick={() => history.push('/outreach')}>
        <ArrowBackIcon fontSize="small" /> Back to Outreach
      </button>

      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1
            className={styles.title}
            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              if (draft.recipient_uuid) {
                history.push(`/outreach/guest/${draft.recipient_uuid}`)
              }
            }}
          >
            {draft.recipient_name || draft.recipient_email}
          </h1>
          <p className={styles.subtitle}>{draft.recipient_email}</p>
        </div>
        <div className={styles.headerBadges}>
          <span className={`${styles.statusBadge} ${getStatusClass(draft.status)}`}>
            {draft.status}
          </span>
          {draft.email_type && (
            <span className={styles.emailTypeBadge}>
              {EMAIL_TYPE_LABELS[draft.email_type] || draft.email_type}
            </span>
          )}
          {isReadyToSend && (
            <span className={styles.readyBadge}>Ready to send</span>
          )}
          {draft.sequence_step && (
            <span className={styles.stepBadge}>Step {draft.sequence_step}/3</span>
          )}
        </div>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Destination</span>
          <span className={styles.infoValue}>{draft.destination || 'N/A'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Trip Start</span>
          <span className={styles.infoValue}>{formatDate(draft.trip_start_date)}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Trip Name</span>
          <span className={styles.infoValue}>{draft.trips?.trip_name || 'N/A'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Trip Type</span>
          <span className={styles.infoValue}>{draft.trips?.trip_type || 'N/A'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Created</span>
          <span className={styles.infoValue}>{formatDate(draft.created_at)}</span>
        </div>
        {draft.scheduled_for && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Recommended Send</span>
            <span className={styles.infoValue}>{formatDateTime(draft.scheduled_for)}</span>
          </div>
        )}
        {draft.confidence_score !== null && draft.confidence_score !== undefined && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>AI Confidence</span>
            <span className={styles.infoValue}>{draft.confidence_score}%</span>
          </div>
        )}
        {draft.ab_variant && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>A/B Variant</span>
            <span className={styles.infoValue}>{draft.ab_variant}</span>
          </div>
        )}
      </div>

      {/* Sequence Timeline */}
      {sequenceDrafts.length > 1 && (
        <div className={styles.sequenceSection}>
          <div className={styles.sectionLabel}>Email Sequence</div>
          <div className={styles.sequenceTimeline}>
            {sequenceDrafts.map((seqDraft: SequenceDraft) => {
              const isActive: boolean = seqDraft.uuid === uuid
              const seqIsReady: boolean =
                seqDraft.status === 'DRAFT' &&
                !!seqDraft.scheduled_for &&
                new Date(seqDraft.scheduled_for) <= new Date()
              return (
                <div
                  key={seqDraft.uuid}
                  className={`${styles.sequenceStep} ${isActive ? styles.sequenceStepActive : ''}`}
                  onClick={() => {
                    if (!isActive) history.push(`/outreach/${seqDraft.uuid}`)
                  }}
                  style={{ cursor: isActive ? 'default' : 'pointer' }}
                >
                  <div className={styles.sequenceStepHeader}>
                    <span className={styles.sequenceStepNumber}>
                      {seqDraft.sequence_step}
                    </span>
                    <span className={styles.sequenceStepType}>
                      {EMAIL_TYPE_LABELS[seqDraft.email_type || ''] || seqDraft.email_type || 'Email'}
                    </span>
                    <span className={`${styles.sequenceStepStatus} ${getStatusClass(seqDraft.status)}`}>
                      {seqDraft.status}
                    </span>
                    {seqIsReady && (
                      <span className={styles.readyBadgeSmall}>Ready</span>
                    )}
                  </div>
                  <div className={styles.sequenceStepSubject}>{seqDraft.email_subject}</div>
                  {seqDraft.sent_at ? (
                    <div className={styles.sequenceStepTime}>
                      Sent {formatDateTime(seqDraft.sent_at)}
                    </div>
                  ) : seqDraft.scheduled_for ? (
                    <div className={styles.sequenceStepTime}>
                      Scheduled: {formatDateTime(seqDraft.scheduled_for)}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {draft.status === 'SENT' && draft.sent_at && (
        <div className={styles.sentInfo}>
          Sent on {formatDate(draft.sent_at)}
        </div>
      )}

      {draft.status === 'SENT' && (
        <div className={styles.trackingSection}>
          <div className={styles.sectionLabel}>Email Tracking</div>
          {loadingEvents ? (
            <div className={styles.trackingLoading}>Loading tracking data...</div>
          ) : trackingEvents.length === 0 ? (
            <div className={styles.trackingEmpty}>No tracking events yet.</div>
          ) : (
            <div className={styles.trackingTimeline}>
              {trackingEvents.map(
                (event: { type: string; received_at: string }, index: number) => (
                  <div key={index} className={styles.trackingEvent}>
                    <span
                      className={`${styles.trackingDot} ${
                        event.type === 'Delivered'
                          ? styles.dotDelivered
                          : event.type === 'Opened'
                            ? styles.dotOpened
                            : event.type === 'Clicked'
                              ? styles.dotClicked
                              : event.type === 'Bounced'
                                ? styles.dotBounced
                                : styles.dotDefault
                      }`}
                    />
                    <span className={styles.trackingType}>{event.type}</span>
                    <span className={styles.trackingTime}>
                      {new Date(event.received_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionLabel}>Subject</div>
        <input
          type="text"
          className={styles.subjectInput}
          value={emailSubject}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailSubject(e.target.value)}
          disabled={!isDraft}
          placeholder="Email subject"
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionLabel}>Email Body</div>
        <textarea
          className={styles.bodyTextarea}
          value={emailBody}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailBody(e.target.value)}
          disabled={!isDraft}
          placeholder="Email body"
        />
        <div className={styles.signaturePreview}>
          <div className={styles.signatureDivider}>--</div>
          <div>Seth Rutner</div>
          <div>Founder & CEO, AvoSquado</div>
          <div>seth@avosquado.com</div>
          <div>https://avosquado.app</div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionLabel}>Internal Notes</div>
        <textarea
          className={styles.notesTextarea}
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
          disabled={!isDraft}
          placeholder="Add internal notes (not sent to recipient)"
        />
      </div>

      {isDraft && hasUnsavedChanges && (
        <div className={styles.unsavedIndicator}>You have unsaved changes — save before sending.</div>
      )}

      {isDraft && (
        <div className={styles.actions}>
          <button
            className={styles.btnSave}
            onClick={handleSave}
            disabled={saving || sending || sendingTest || regenerating}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          {canRegenerate && (
            <button
              className={styles.btnRegenerate}
              onClick={handleRegenerate}
              disabled={saving || sending || sendingTest || regenerating || hasUnsavedChanges}
            >
              {regenerating ? 'Regenerating...' : 'Re-generate'}
            </button>
          )}
          <div className={styles.testEmailGroup}>
            <input
              type="email"
              className={styles.testEmailInput}
              value={testEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestEmail(e.target.value)}
              placeholder="Test email address"
            />
            <button
              className={styles.btnTest}
              onClick={handleSendTest}
              disabled={saving || sending || sendingTest || regenerating || !emailSubject || !emailBody || !testEmail || hasUnsavedChanges}
            >
              {sendingTest ? 'Sending...' : 'Send Test'}
            </button>
          </div>
          <button
            className={styles.btnSend}
            onClick={() => setShowSendModal(true)}
            disabled={saving || sending || sendingTest || regenerating || !emailSubject || !emailBody || hasUnsavedChanges}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
          <button
            className={styles.btnSkip}
            onClick={() => setShowSkipModal(true)}
            disabled={saving || sending || sendingTest || regenerating}
          >
            Skip
          </button>
        </div>
      )}
    </div>
  )
}

export default OutreachDetailPage
